import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { LOGO_URL, BRAND_NAME, BRAND_TAGLINE } from "@/lib/brand";
import SocialLinks from "@/components/SocialLinks";
import VerifyBanner from "@/components/VerifyBanner";
import FloatingDonate from "@/components/FloatingDonate";
import ResourceTicker from "@/components/ResourceTicker";

const navLinks = [
  { to: "/", label: "Home", slug: "home" },
  { to: "/about", label: "Our Story", slug: "story" },
  { to: "/travels", label: "On the Road", slug: "travels" },
  { to: "/stories", label: "Heroes", slug: "heroes" },
  { to: "/events", label: "Events", slug: "events" },
  { to: "/journal", label: "Journal", slug: "journal" },
  { to: "/podcast", label: "Real Stories", slug: "real-stories" },
  { to: "/album", label: "Photos", slug: "album" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      <header className="glass-header sticky top-0 z-40">
        <VerifyBanner />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <div className="relative shrink-0">
              <div className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-[var(--terracotta)] via-[var(--sand)] to-[var(--forest)] opacity-70 group-hover:opacity-100 blur-[1px] transition-opacity duration-300" />
              <img src={LOGO_URL} alt={BRAND_NAME}
                className="relative w-14 h-14 rounded-full object-cover ring-2 ring-[var(--bg)] transition-transform duration-300 group-hover:scale-105" />
            </div>
            <div className="leading-none">
              <div className="font-serif-display text-2xl md:text-[26px] font-medium tracking-[-0.01em] italic text-[var(--ink)]">
                {BRAND_NAME}
              </div>
              <div className="font-mono-acc text-[10px] tracking-[0.32em] uppercase mt-1.5 flex items-center gap-2">
                <span className="inline-block w-3 h-px bg-[var(--terracotta)]" />
                <span className="text-[var(--terracotta)] font-semibold">{BRAND_TAGLINE}</span>
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.slug}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-[var(--terracotta)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/donate"
              data-testid="header-donate"
              className="inline-flex items-center gap-1.5 text-sm bg-[var(--terracotta)] text-[var(--ink)] px-4 py-2 rounded-full hover:bg-[var(--terracotta-dark)] transition-colors font-medium"
            >
              <span className="text-[var(--sand)]">♥</span> Donate
            </Link>
            {user && user !== false ? (
              <>
                <Link to="/messages" data-testid="nav-link-messages" className="text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]">
                  Messages
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-link-admin" className="text-sm text-[var(--forest)] font-medium">
                    Admin
                  </Link>
                )}
                <button
                  data-testid="logout-button"
                  onClick={() => { logout(); navigate("/"); }}
                  className="text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-link-login" className="text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]">
                  Sign in
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-link-register"
                  className="text-sm bg-[var(--terracotta)] text-[var(--ink)] px-5 py-2 rounded-full hover:bg-[var(--terracotta-dark)] transition-colors"
                >
                  Join us
                </Link>
              </>
            )}
          </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--line)] bg-[var(--bg)]">
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.slug}`}
                  className="text-base py-2">{l.label}</NavLink>
              ))}
              <Link to="/donate" onClick={() => setOpen(false)} data-testid="mobile-donate"
                className="text-base py-2 text-[var(--terracotta)] font-medium">♥ Donate</Link>
              {user && user !== false ? (
                <>
                  <Link to="/messages" onClick={() => setOpen(false)} className="text-base py-2">Messages</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-base py-2">Admin</Link>}
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-base py-2 text-left">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-base py-2">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="text-base py-2">Join us</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>
      <FloatingDonate />

      <ResourceTicker />

      <footer className="border-t border-[var(--line)]" style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-3 gap-8 text-sm text-[var(--ink-soft)]">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <img src={LOGO_URL} alt={BRAND_NAME} className="w-9 h-9 rounded-full object-cover" />
              <span className="font-serif-display text-lg text-[var(--ink)]">{BRAND_NAME}</span>
            </div>
            <p className="leading-relaxed">Two hearts in recovery. One road across America. A movement that says <em className="text-[var(--terracotta)] not-italic">sobriety is possible</em>.</p>
            <div className="mt-5">
              <SocialLinks variant="footer" label="Follow us" />
            </div>
          </div>
          <div>
            <div className="overline mb-3">Explore</div>
            <ul className="space-y-2">
              <li><Link to="/travels" className="hover:text-[var(--ink)]">On the Road</Link></li>
              <li><Link to="/podcast" className="hover:text-[var(--ink)]">Real Stories</Link></li>
              <li><Link to="/resources" className="hover:text-[var(--ink)]">Resources</Link></li>
              <li><Link to="/press" className="hover:text-[var(--ink)]">Press kit</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">If you&apos;re struggling</div>
            <p>Send us a private message. We read every one. You are not alone, and you are not too far gone.</p>
            <p className="mt-3 text-xs">In crisis? Call <strong className="text-[var(--ink)]">SAMHSA 1-800-662-4357</strong> — free, confidential, 24/7.</p>
          </div>
        </div>
        <div className="border-t border-[var(--line)] py-5 text-center text-xs text-[var(--ink-soft)]">
          © {new Date().getFullYear()} {BRAND_NAME} · A movement built on hope
        </div>
      </footer>
    </div>
  );
}
